import periodsLibrary, { FilterType, FilterTypeValue } from "@/components/filter/filter.types.ts";
import { useAppFilter } from "@/components/filter/use-filter.hook.tsx";
import { ChoosePeriodModal } from "@/components/filter/ChoosePeriodModal.tsx";

import {
  useModals,
  ApiAggregationDate,
  Button,
  DropdownMenu,
  DropdownMenuItem,
  FilterItem,
  FontAwesomeIcon,
  parseUrlHelper,
  PeriodLibraryItem,
} from "@skailer-com/uikit";

import useFormatter from "@/hooks/use-formatter.hook.ts";
import { getApiAggregationDateTitle } from "@/types/analytics/analytics.types.ts";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

export interface Props {
  filtersConfig: FilterItem<FilterTypeValue>[];
  availableAggregationDateModes?: ApiAggregationDate[];
  staticPeriods?: PeriodLibraryItem[];
}

export const FilterChoosePeriod = ({
  filtersConfig,
  availableAggregationDateModes = [],
  staticPeriods,
}: Props) => {
  const { formatPeriodDates } = useFormatter();
  const { t } = useTranslation();
  const modalsService = useModals();
  const [, setSearchParams] = useSearchParams();

  const { getFilterDates, getAggregationDateMode } = useAppFilter({
    filters: filtersConfig,
    source: "—",
  });

  const dateFromFilter = filtersConfig.find((filter) => filter.type === FilterType.dateFrom);
  const dateToFilter = filtersConfig.find((filter) => filter.type === FilterType.dateTo);
  const aggregationDateModeFilter = filtersConfig.find(
    (filter) => filter.type === FilterType.aggregationDateMode
  );

  const dateFromFilterKey = dateFromFilter?.searchParamName || dateFromFilter?.name || "date_from";
  const dateToFilterKey = dateToFilter?.searchParamName || dateToFilter?.name || "date_to";

  const aggregationType = aggregationDateModeFilter
    ? getAggregationDateMode(aggregationDateModeFilter)
    : ApiAggregationDate.month;

  const selectedPeriodDates = getFilterDates();

  const availablePeriodOptions = useMemo(() => {
    if (staticPeriods) return staticPeriods;

    const availableOptionsRaw = Object.values(periodsLibrary()).filter((x) =>
      x.aggregation_date_modes.includes(aggregationType)
    );
    return availableOptionsRaw.map((x) => {
      return {
        title: x.title,
        start: x.start,
        end: x.end,
        key: x.key,
      };
    });
  }, [aggregationType]);

  const selectedPeriodOption = useMemo(() => {
    return availablePeriodOptions.find(
      (option) =>
        option.start.getTime() == selectedPeriodDates?.dateFrom?.getTime() &&
        option.end.getTime() == selectedPeriodDates?.dateTo?.getTime()
    );
  }, [aggregationType, selectedPeriodDates]);

  const handleCustomPeriod = useCallback(() => {
    modalsService.openModal(ChoosePeriodModal, {
      dateFrom: selectedPeriodDates?.dateFrom,
      dateTo: selectedPeriodDates?.dateTo,
      onApply: (dfrom: Date, dto: Date) => {
        setSearchParams((searchParams) => {
          searchParams.set(dateFromFilterKey, parseUrlHelper.formatDate(dfrom));
          searchParams.set(dateToFilterKey, parseUrlHelper.formatDate(dto));
          searchParams.delete("period");
          return searchParams;
        });
      },
    });
  }, [selectedPeriodDates]);

  const aggregationDateOptions = useMemo(() => {
    if (availableAggregationDateModes.length == 0) {
      return null;
    }

    return availableAggregationDateModes.map((item) => {
      return {
        id: item,
        title: getApiAggregationDateTitle(item),
      };
    });
  }, [availableAggregationDateModes]);

  const selectedAggregationModeOption = useMemo(
    () => aggregationDateOptions?.find((option) => option.id === aggregationType),
    [aggregationType]
  );

  return (
    <>
      {aggregationDateModeFilter && aggregationDateOptions && (
        <DropdownMenu
          trigger={
            <Button
              variant={"secondary"}
              rightIcon={<FontAwesomeIcon icon={"fa-light fa-chevron-down"} />}
            >
              {selectedAggregationModeOption?.title}
            </Button>
          }
        >
          {aggregationDateOptions.map((option) => (
            <DropdownMenuItem
              key={`choose-group-period-${option.id} `}
              title={option.title}
              isSelected={aggregationType === option.id}
              onClick={() => {
                setSearchParams((searchParams) => {
                  searchParams.set(
                    aggregationDateModeFilter.searchParamName || aggregationDateModeFilter.name,
                    option.id.toString()
                  );
                  return searchParams;
                });
              }}
            />
          ))}
        </DropdownMenu>
      )}

      <DropdownMenu
        trigger={
          <Button
            variant={"secondary"}
            rightIcon={<FontAwesomeIcon icon={"fa-light fa-chevron-down"} />}
          >
            {selectedPeriodOption
              ? selectedPeriodOption.title
              : formatPeriodDates({
                  dateStart: selectedPeriodDates?.dateFrom,
                  dateEnd: selectedPeriodDates?.dateTo,
                })?.title}
          </Button>
        }
      >
        {availablePeriodOptions.map((option) => (
          <DropdownMenuItem
            key={`choose-period-${option.title} `}
            title={option.title}
            isSelected={
              option.start.getTime() == selectedPeriodDates?.dateFrom?.getTime() &&
              option.end.getTime() == selectedPeriodDates?.dateTo?.getTime()
            }
            onClick={() => {
              setSearchParams((searchParams) => {
                searchParams.delete(dateFromFilterKey);
                searchParams.delete(dateToFilterKey);
                searchParams.set("period", option.key);
                return searchParams;
              });
            }}
          />
        ))}
        <DropdownMenuItem
          title={t("common:custom_period")}
          isSelected={!selectedPeriodDates}
          onClick={handleCustomPeriod}
        />
      </DropdownMenu>
    </>
  );
};
